import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { errorMessage } from "../../utils/errorMessage";
import { EMPTY, count, decimal, fetchDetail, rangeCaption } from "../../utils/adminAnalyticsApi";

/**
 * The list behind an analytics number, in a panel over the page.
 *
 * <p>A panel rather than a separate screen, so opening a list never loses the
 * tab and date range you were looking at. One panel for the whole console,
 * opened through context, so a card only has to say which metric it is — it
 * does not need to know how the list is fetched, rendered or closed.
 *
 * <p>Every name in a list links to that patient's chart. Opening the chart goes
 * through the server's normal access check and writes its own audit row naming
 * the patient. The list itself is logged too, with a row naming each patient it
 * showed -- and if the server cannot write that, it refuses the list (503) and
 * the panel shows the error rather than the names.
 *
 * <p>A closed account arrives as "Closed account" with no patientId, and so
 * renders as plain text with no link.
 */

const DrillDownContext = createContext(null);

/** Whether a drill-down is available here, and how to open one. */
export const useDrillDown = () => useContext(DrillDownContext);

/** Wraps the analytics console so any card inside it can open a list. */
export const DrillDownProvider = ({ range, children }) => {
  const [metric, setMetric] = useState(null);
  // The element that opened the panel, so focus can go back to it on close.
  // Without this, a keyboard user who closes the panel is dropped at the top of
  // the page and has to tab all the way back down to where they were.
  const returnFocusTo = useRef(null);

  const open = useCallback((key) => {
    returnFocusTo.current = document.activeElement;
    setMetric(key);
  }, []);

  const close = useCallback(() => {
    setMetric(null);
    const target = returnFocusTo.current;
    returnFocusTo.current = null;
    if (target && typeof target.focus === "function") {
      // After the panel unmounts, or focus lands on something being removed.
      setTimeout(() => target.focus(), 0);
    }
  }, []);

  return (
    <DrillDownContext.Provider value={{ open }}>
      {children}
      {metric ? <DetailPanel metric={metric} range={range} onClose={close} /> : null}
    </DrillDownContext.Provider>
  );
};

/** How a single cell renders, by the column type the server declared. */
const renderCell = (column, row) => {
  const value = row[column.key];
  switch (column.type) {
    case "patient": {
      // The id comes from the row's own patientId, never from position. A list
      // whose names linked to the wrong chart would be worse than no links.
      const patientId = row.patientId;
      if (!value) return EMPTY;
      if (patientId === null || patientId === undefined) return value;
      return (
        <Link
          className="analytics-detail-patient"
          to="/admin-view-patient"
          state={{ userId: patientId }}
        >
          {value}
        </Link>
      );
    }
    case "money":
      return exactMoney(value);
    case "number":
      // Signed, because a symptom change can be negative and that matters.
      if (value === null || value === undefined) return EMPTY;
      return decimal(value);
    case "date":
      return formatWhen(value, false);
    case "datetime":
      return formatWhen(value, true);
    default:
      return value === null || value === undefined || value === "" ? EMPTY : String(value);
  }
};

/**
 * Cents as dollars AND cents. The cards round to whole dollars because they are
 * totals; a list is where someone reconciles against Stripe, line by line, and
 * a $49.99 payment shown as "$50" would not match anything there.
 */
export const exactMoney = (cents) => {
  if (cents === null || cents === undefined) return EMPTY;
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Whether a response is a list the panel can draw. Anything else -- an HTML
 * error page from a proxy, a changed API -- is shown as an error rather than
 * thrown during render, which would blank the whole analytics console.
 */
const isDetail = (detail) =>
  !!detail && Array.isArray(detail.columns) && Array.isArray(detail.rows);

/** Everything inside the panel that Tab can land on, in order. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A date or timestamp from the server, in the admin's own locale.
 *
 * A bare date ("2026-09-30") is parsed at local noon, not midnight. Parsed as
 * UTC midnight it renders as the previous day anywhere west of Greenwich — so
 * every appointment on this screen would show a day early in South Carolina.
 */
const formatWhen = (value, withTime) => {
  if (!value) return EMPTY;
  const text = String(value);
  const isBareDate = /^\d{4}-\d{2}-\d{2}$/.test(text);
  const when = new Date(isBareDate ? `${text}T12:00:00` : text.replace(" ", "T"));
  if (Number.isNaN(when.getTime())) return text;
  return withTime
    ? when.toLocaleString("en-US", {
        year: "numeric", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      })
    : when.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const DetailPanel = ({ metric, range, onClose }) => {
  const [state, setState] = useState({ loading: true, error: "", detail: null });
  const panelRef = useRef(null);
  const titleId = `analytics-detail-title-${metric}`;

  const load = useCallback(async () => {
    setState({ loading: true, error: "", detail: null });
    try {
      const detail = await fetchDetail(metric, range);
      if (!isDetail(detail)) {
        setState({ loading: false, error: "Could not load this list.", detail: null });
        return;
      }
      setState({ loading: false, error: "", detail });
    } catch (err) {
      setState({
        loading: false,
        error: errorMessage(err, "Could not load this list."),
        detail: null,
      });
    }
  }, [metric, range]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Focus into the panel on open, so a screen reader announces it and the
    // keyboard is inside it rather than behind it.
    panelRef.current?.focus();

    const onKey = (event) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      // Keep Tab inside the panel. aria-modal tells a screen reader the page
      // behind is inert, but the browser does not enforce it: without this,
      // tabbing past the last link walks into the cards hidden behind the
      // backdrop, where nothing visible has focus.
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
      if (focusable.length === 0) {
        event.preventDefault();
        panelRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const inside = panelRef.current.contains(active);
      if (event.shiftKey && (active === first || active === panelRef.current || !inside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !inside)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);

    // The page behind must not scroll while the panel is up, or a long list
    // and the page fight over the same scroll wheel.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const { loading, error, detail } = state;
  const visibleColumns = detail ? detail.columns.filter((c) => c.type !== "hidden") : [];

  return (
    <div
      className="analytics-detail-backdrop"
      // Clicking outside the panel closes it. The panel stops the click from
      // reaching here, so clicking inside a table cell does not.
      onClick={onClose}
    >
      <div
        className="analytics-detail-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={panelRef}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="analytics-detail-header">
          <div>
            <h3 id={titleId} className="analytics-detail-title">
              {detail ? detail.title : "Loading…"}
            </h3>
            {detail?.range ? (
              <p className="analytics-detail-caption">{rangeCaption(detail.range)}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="analytics-detail-close"
            onClick={onClose}
            aria-label="Close this list"
          >
            ×
          </button>
        </header>

        {detail?.note ? <p className="analytics-detail-note">{detail.note}</p> : null}

        {loading ? (
          <p className="analytics-detail-status" role="status">
            Loading…
          </p>
        ) : null}

        {error ? (
          <div className="analytics-error" role="alert">
            <p>{error}</p>
            <button type="button" className="analytics-retry" onClick={load}>
              Try again
            </button>
          </div>
        ) : null}

        {detail && detail.rows.length === 0 ? (
          <p className="analytics-empty">Nobody in this list for this period.</p>
        ) : null}

        {detail && detail.rows.length > 0 ? (
          <>
            <p className="analytics-detail-count">
              {detail.truncated
                ? `Showing the first ${count(detail.rows.length)} — there are more than ${count(
                    detail.rowLimit
                  )}. Narrow the date range to see the rest.`
                : `${count(detail.rows.length)} ${detail.rows.length === 1 ? "row" : "rows"}`}
            </p>
            <div className="analytics-table-wrap">
              <table className="analytics-table analytics-detail-table">
                <thead>
                  <tr>
                    {visibleColumns.map((column) => (
                      <th key={column.key} scope="col">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detail.rows.map((row, index) => (
                    <tr key={`${row.patientId ?? "row"}-${index}`}>
                      {visibleColumns.map((column) => (
                        <td
                          key={column.key}
                          className={
                            column.type === "money" || column.type === "number"
                              ? "analytics-detail-numeric"
                              : undefined
                          }
                        >
                          {renderCell(column, row)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default DetailPanel;
